// Shifts, cash and the day close: a staff member's till session, its cash movements, its blind
// close with the variance, and the business-day close that reconciles the day's takings across
// tenders (F-OPS-01 Open shift with float, F-OPS-02 Cash movements, F-OPS-03 Close shift with the
// blind count and variance, F-OPS-04 Shift X/Z report, F-OPS-05 Hand-over, F-OPS-06 Business-day
// close, F-OPS-07 Variance alerts; spec/13 §10, spec/06 §2.4).
//
//   POST …/venues/{v}/shifts                    open a till session with an opening float (F-OPS-01)
//   GET  …/venues/{v}/shifts                    the venue's shifts, newest first (F-OPS-04)
//   GET  …/venues/{v}/shifts/current            the venue's open shift, or 404 when none is (F-OPS-01)
//   GET  …/venues/{v}/shifts/{id}               one shift with its movements and X/Z report (F-OPS-04)
//   GET  …/venues/{v}/shifts/{id}/cash-timeline every drawer event in order, with the running expected drawer (F-OPS-04)
//   POST …/venues/{v}/shifts/{id}/movements     a pay-in, a pay-out or a drop to the safe (F-OPS-02)
//   POST …/venues/{v}/shifts/{id}/close         the blind count, the variance, and the hand-over (F-OPS-03/-05)
//   GET  …/venues/{v}/day-close                 the business day's takings by tender, for review (F-OPS-06)
//   POST …/venues/{v}/day-close                 close the business day once every shift is closed (F-OPS-06)
//   POST …/venues/{v}/drawer/open               Open drawer: a no-sale kick through the receipt printer (F-OPS-09)
//
// Money is an integer minor unit plus an ISO code everywhere (spec/06 §1). The drawer reconciles
// through the ledger and never invents money (spec/06 §2.4 invariant 6): the opening float and every
// cash movement post a balanced journal entry, the day's takings are **read** from the tender
// accounts the ledger already holds, and the close records the variance rather than adjusting a
// balance by hand.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { BusinessDayCloseIdSchema, CashMovementIdSchema, CounterIdSchema, MemberIdSchema, PrinterIdSchema, SaleIdSchema, ShiftIdSchema, StationCommandIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema, PositiveMoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema, LocalDateSchema, LocalTimeSchema, TimezoneSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { OTHERS_PERMISSION_EXTENSION, PERMISSION_EXTENSION } from "./audit.js";
import { IdempotencyHeaderSchema } from "./id-checks.js";
import { SHIFTS_CASH_MOVEMENTS, SHIFTS_CLOSE_ANY, SHIFTS_DAY_CLOSE, SHIFTS_DRAWER_OPEN, SHIFTS_OWN, SHIFTS_READ_ALL, VENUES_READ, } from "../permissions/index.js";
import { StationCommandStatusSchema } from "../realtime/commands.js";
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/13 §10, spec/06 §9.1)
// ---------------------------------------------------------------------------------------------
/** Whether a till session is open or has been closed and counted (spec/13 §10). */
export const ShiftStatusSchema = z
    .enum(['open', 'closed'])
    .openapi('ShiftStatus', { description: "A till session's state (spec/13 §10)." });
/**
 * A cash movement on the open drawer (spec/13 §10, F-OPS-02). `pay_in` brings cash into the drawer
 * (petty-cash return, change from the bank), `pay_out` takes it out (petty cash, a supplier), and a
 * `drop` moves it to the safe.
 */
export const CashMovementKindSchema = z
    .enum(['pay_in', 'pay_out', 'drop'])
    .openapi('CashMovementKind', { description: 'A cash movement on a shift drawer (spec/13 §10).' });
/**
 * A tender counted against the drawer at close (spec/13 §10). Cash is the drawer itself; counter
 * card and counter UPI are confirmed against their terminals and apps. Online payments are listed
 * for reference but are not in the drawer, so they are not one of these.
 */
export const ShiftTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi'])
    .openapi('ShiftTender', { description: 'A tender reconciled at shift close (spec/13 §10).' });
/** Whether the report is an interim X (during the shift) or the final Z (at close) (spec/13 §10). */
export const ShiftReportKindSchema = z
    .enum(['x', 'z'])
    .openapi('ShiftReportKind', { description: 'An X (during) or Z (at close) shift report (spec/13 §10, F-OPS-04).' });
/**
 * How a venue's business day ends (`shifts.dayCloseMode`, ADR-0029): `manual` — staff review and close it, once every
 * shift of the day is counted (the default, and what every venue did before 2026-10-01); `auto` — the day rolls over
 * at the venue's cutoff by itself, the worker files its close at rollover, and a manager signs it off later. A
 * 24×7 venue uses `auto` with the cutoff at its quietest hour. On a closed day it says which of the two closed it.
 */
export const DayCloseModeSchema = z
    .enum(['manual', 'auto'])
    .openapi('DayCloseMode', { description: 'How a business day ends: closed by staff, or rolled over automatically at the cutoff (ADR-0029).' });
/**
 * How a venue handles cash between people (`shifts.cashHandling`, ADR-0029): `per_drawer` — a shift per counter's drawer,
 * several counters at once (the default); `shared_drawer` — one drawer, one shift, colleagues join it and the count is
 * the shift's; `floor_without_drawer` — members without a drawer sell by card, UPI and wallet, and their cash needs an
 * open drawer.
 */
export const CashHandlingSchema = z
    .enum(['per_drawer', 'shared_drawer', 'floor_without_drawer'])
    .openapi('CashHandling', { description: 'How a venue handles cash between people (ADR-0029).' });
/** Someone on a shift's drawer, and when (ADR-0029): the opener from the start, and colleagues who join a shared one. */
export const ShiftMemberSchema = z
    .object({
    memberId: MemberIdSchema,
    joinedAt: IsoDateTimeSchema,
    /** When they left the drawer, or the shift ended; null while on it. */
    leftAt: IsoDateTimeSchema.nullable(),
})
    .openapi('ShiftMember');
// ---------------------------------------------------------------------------------------------
// A shift (spec/13 §10)
// ---------------------------------------------------------------------------------------------
/** One cash movement recorded on a shift's drawer (spec/13 §10, F-OPS-02). Append-only. */
export const CashMovementSchema = z
    .object({
    id: CashMovementIdSchema,
    kind: CashMovementKindSchema,
    amount: PositiveMoneySchema,
    reason: z.string().min(1).max(200),
    recordedByMemberId: MemberIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('CashMovement');
/**
 * One tender's reconciliation at close (spec/13 §10): what the ledger expected, what was counted,
 * and the variance. `counted` and `variance` are null while the shift is open — blind close hides
 * the expected amounts until the count is entered (`shifts.blindClose`).
 */
export const ShiftTenderCountSchema = z
    .object({
    tender: ShiftTenderSchema,
    expected: MoneySchema,
    counted: MoneySchema.nullable(),
    variance: MoneySchema.nullable(),
})
    .openapi('ShiftTenderCount');
/**
 * A till session, its cash movements and its X/Z report (spec/13 §10). The `expected*` figures are
 * read from the ledger's tender accounts, never invented (spec/06 §2.4 invariant 6): the expected
 * cash is the opening float plus the shift's cash sales and pay-ins, less its pay-outs and drops.
 */
export const ShiftSchema = z
    .object({
    id: ShiftIdSchema,
    venueId: z.string(),
    status: ShiftStatusSchema,
    /** A label for the till or counter this session runs at (device pairing is a follow-on). */
    counterName: z.string().min(1).max(60).nullable(),
    /** The venue business day the shift opened in (spec/01), the key its takings file under. */
    businessDay: LocalDateSchema,
    openedByMemberId: MemberIdSchema.nullable(),
    openedAt: IsoDateTimeSchema,
    openingFloat: NonNegativeMoneySchema,
    closedByMemberId: MemberIdSchema.nullable(),
    closedAt: IsoDateTimeSchema.nullable(),
    closingNote: z.string().min(1).max(2000).nullable(),
    /** The closed shift whose counted cash opened this one (hand-over, F-OPS-05); null otherwise. */
    carriedFromShiftId: ShiftIdSchema.nullable(),
    /** The shift opened with this one's counted cash at hand-over (F-OPS-05); null otherwise. */
    carriedToShiftId: ShiftIdSchema.nullable(),
    /**
     * The business day the shift's count and variance are reported on (ADR-0029): the day it opened, or — when that day
     * rolled over by itself while the shift was still open (`shifts.dayCloseMode: auto`) — the first day still open
     * when it was counted. A shift may span the cutoff; its takings are filed by payment time either way. Null while
     * open.
     */
    countBusinessDay: LocalDateSchema.nullable(),
    /** Who has been on the drawer, and when (ADR-0029); empty on a shift opened before members were kept. */
    members: z.array(ShiftMemberSchema),
    movements: z.array(CashMovementSchema),
    reportKind: ShiftReportKindSchema,
    /** The cash the ledger expects in the drawer: float + cash sales + pay-ins − pay-outs − drops. */
    expectedCash: MoneySchema,
    /** What the drawer counted at close; null while the shift is open. */
    countedCash: MoneySchema.nullable(),
    /** `counted − expected`; null while the shift is open (spec/13 §10). */
    cashVariance: MoneySchema.nullable(),
    /** The cash taken through sales during the shift, read from the ledger (for the report). */
    cashSales: MoneySchema,
    /**
     * The cash customers handed over for the shift's cash sales, before change was given back: `cashSales + changeGiven`
     * (ADR-0049). A figure for the report only; the drawer's expected cash is unchanged by it.
     */
    cashReceived: MoneySchema,
    /** The change handed back from the drawer on the shift's cash sales (each payment's `change`, ADR-0049). */
    changeGiven: MoneySchema,
    /** How many sales paid into this drawer during the shift, by any counter tender. */
    salesCount: z.int().min(0),
    /** The opener's and the closer's names, when the member can be read; null otherwise. */
    openedByName: z.string().nullable(),
    closedByName: z.string().nullable(),
    /** Expected vs counted per tender, including counter card and UPI (spec/13 §10). */
    tenders: z.array(ShiftTenderCountSchema),
    /**
     * Whether the close's cash variance exceeded `shifts.varianceAlertAmount` (F-OPS-07) — an overage the same as a
     * shortage — and so raised N-23 and `alerts.new`. Always false while the shift is open.
     */
    varianceAlerted: z.boolean(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('Shift');
export const ShiftListSchema = z.object({ shifts: z.array(ShiftSchema) }).openapi('ShiftList');
// ---------------------------------------------------------------------------------------------
// The business-day close (spec/13 §10, F-OPS-06)
// ---------------------------------------------------------------------------------------------
/**
 * A tender the business day's takings are totalled by (spec/13 §10, F-OPS-06): the drawer's three and the wallet
 * spent at the venue. Online payments settle to the organisation's provider clearing and are not a venue's
 * takings here.
 */
export const DayTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'wallet'])
    .openapi('DayTender', { description: "A tender the business day's takings are totalled by (spec/13 §10)." });
/** One tender's takings for the business day, net of refunds, read from the ledger (spec/06 §2.4 invariant 6). */
export const DayTenderTakingSchema = z
    .object({ tender: DayTenderSchema, amount: MoneySchema })
    .openapi('DayTenderTaking');
/** A sales category on the day-close review: the revenue account a sale line settles to (spec/06 §2.1). */
export const DayCategorySchema = z
    .enum(['play', 'food', 'retail', 'other'])
    .openapi('DayCategory', { description: 'A sales category on the day-close review (spec/13 §10).' });
/** One category's sales for the business day, before tax and refunds, read from the ledger. */
export const DayCategoryTakingSchema = z
    .object({ category: DayCategorySchema, amount: MoneySchema })
    .openapi('DayCategoryTaking');
/** A count of things and the money they came to — the day's voids or refunds. */
export const DayCountedAmountSchema = z
    .object({ count: z.int().min(0), amount: MoneySchema })
    .openapi('DayCountedAmount');
/** The comp time the day's sessions were granted (F-SES-16): how many sessions, and how many seconds in all. */
export const DayCompsSchema = z
    .object({ sessions: z.int().min(0), seconds: z.int().min(0) })
    .openapi('DayComps');
/**
 * How the day's sales by category become its revenue (spec/13 §10, 2026-10-01): every term read from the same ledger
 * cells as M-01, so `sales − discounts + tax + taxPaidAtTopUp − refunds − onAccount − walletBonus − walletSettling − other = revenue`
 * exactly (`dayRevenueBridgeOf` in `@clavian/domain`). A term may be negative: an older on-account debt settled today
 * makes `onAccount` negative, which adds to revenue.
 */
export const DayRevenueBridgeSchema = z
    .object({
    /** The day's sales by category, summed: ex-tax, before discounts, refunds and wallet credit apart. */
    sales: MoneySchema,
    /** Discounts given on those sales (refunds' releases apart). */
    discounts: MoneySchema,
    /** Tax collected on them (`tax_payable:*`); zero at an untaxed venue. */
    tax: MoneySchema,
    /**
     * GST on sales paid with balances moved in from an old system as GST already paid at top-up (ADR-0048): part of
     * what players paid, not tax owed today. Zero unless such balances were spent.
     */
    taxPaidAtTopUp: MoneySchema,
    /** Money given back by the day's refunds, of sales and of passes. */
    refunds: MoneySchema,
    /** Sold on account and not paid today: receivables raised less settled and written off. */
    onAccount: MoneySchema,
    /** Paid with wallet bonus: promotional credit, not money. */
    walletBonus: MoneySchema,
    /** Wallet payments taken for sales that have not settled yet; zero once every sale settles. */
    walletSettling: MoneySchema,
    /** Anything else the day's entries touched (rounding, write-offs, fees). Usually zero. */
    other: MoneySchema,
    /** M-01 Revenue — the same figure as the review's `revenue`. */
    revenue: MoneySchema,
})
    .openapi('DayRevenueBridge');
/**
 * What the manager reviews before closing the day (spec/13 §10): takings by tender and by category, voids and
 * refunds, discounts and comps, receivables raised, pass sales and wallet top-ups. Everything money is read from
 * the ledger inside the business day's window (the venue's cutoff, in its timezone); voids come from the day's
 * cancelled sales and comps from its sessions, because neither moves money.
 */
const dayTotalsShape = {
    /** Takings by tender, net of the day's refunds (`tender_*` accounts). */
    tenders: z.array(DayTenderTakingSchema),
    /**
     * Sales by category before tax and discounts, refunds reported apart (`sales_*` net credits of the sales). Wallet
     * credit the venue gives away (a top-up bonus, a coupon grant, a manual credit) is not a sale: it is `walletCreditGiven`.
     */
    categories: z.array(DayCategoryTakingSchema),
    /** Refunds settled in the day, whatever day the sale was on (a closed day's corrections land here). */
    refunds: DayCountedAmountSchema,
    /** Sales cancelled before payment that were placed on this day (`orders:void`). */
    voids: DayCountedAmountSchema,
    /** Discounts given on the day's settled sales (the `discounts` contra account), net of refunds. */
    discounts: MoneySchema,
    /** Comp time granted to sessions that started on the day (F-SES-16). */
    comps: DayCompsSchema,
    /** Money players were put on account for on the day: on-account tenders and postpaid play (`receivable`). */
    receivablesRaised: MoneySchema,
    /** Passes sold on the day, before tax (the `sales_play` credit of pass sales and pass lines). */
    passSales: MoneySchema,
    /** Wallet top-ups taken at the venue's tenders on the day (online top-ups settle to the organisation). */
    walletTopUps: MoneySchema,
    /**
     * Wallet credit the venue gave away on the day (2026-10-01): top-up bonuses, coupons' wallet grants and manual wallet
     * credits, less manual debits. Promotional, not money taken; it costs revenue only when spent (`revenueBridge.walletBonus`).
     * Zero on a day closed before it was kept.
     */
    walletCreditGiven: MoneySchema,
    /** The sum of the day's shifts' cash variances (spec/13 §10). */
    totalCashVariance: MoneySchema,
    /**
     * M-01 Revenue for the day (spec/17 §3, F-ANL-01): settled payments minus refunds, wallet top-ups excluded (counted
     * when the wallet is spent), on-account counted when settled — the canonical figure every report shows for this day.
     * The tenders above are the drawer's view (a cash top-up is in the till); this is what the business earned. Null
     * with no payment on the day, and on a day closed before the figure was kept (2026-10-01).
     */
    revenue: MoneySchema.nullable(),
    /**
     * How `categories` become `revenue` (2026-10-01): null with no revenue, and on a day closed before it was kept.
     */
    revenueBridge: DayRevenueBridgeSchema.nullable(),
    /**
     * The day's money in (2026-10-02, the Reports' one revenue definition, `revenueBreakdownOf`): cash, counter card,
     * counter UPI and online taken — wallet top-ups included — less money handed back. The wallet tender above is
     * **not** money in (its money came in when the wallet was topped up), so this is not the tenders' sum. A day closed
     * before it was kept answers its cash, card and UPI.
     */
    moneyIn: MoneySchema,
    /** Revenue paid from wallets on the day (wallet cash spent on play, food and passes): part of `revenue`. */
    walletSpent: MoneySchema,
    /** Bonus credit spent on the day: a promotional cost, never revenue (= `revenueBridge.walletBonus`). */
    bonusSpent: MoneySchema,
};
/** A shift on the day-close review — compact, with its count, its variance and who ran it (spec/13 §10). */
export const DayCloseShiftSchema = z
    .object({
    id: ShiftIdSchema,
    status: ShiftStatusSchema,
    counterName: z.string().min(1).max(60).nullable(),
    openedByMemberId: MemberIdSchema.nullable(),
    /** The cashier's name, when the member still exists; null otherwise. */
    openedByName: z.string().nullable(),
    openedAt: IsoDateTimeSchema,
    closedByMemberId: MemberIdSchema.nullable(),
    closedAt: IsoDateTimeSchema.nullable(),
    /** What the ledger expected in the drawer at close; null while open. */
    expectedCash: MoneySchema.nullable(),
    /** What the drawer counted at close; null while open. */
    countedCash: MoneySchema.nullable(),
    cashVariance: MoneySchema.nullable(),
    /** Whether the variance raised the owner's alert (F-OPS-07). */
    varianceAlerted: z.boolean(),
})
    .openapi('DayCloseShift');
/**
 * The business day before it is closed (spec/13 §10, F-OPS-06): the totals the manager reviews, the day's shifts
 * and their variances, and whether every shift is closed — a day cannot be closed while one is open.
 */
export const DayCloseReviewSchema = z
    .object({
    venueId: z.string(),
    businessDay: LocalDateSchema,
    closed: z.boolean(),
    allShiftsClosed: z.boolean(),
    openShiftCount: z.int().min(0),
    ...dayTotalsShape,
    shifts: z.array(DayCloseShiftSchema),
    closedByMemberId: MemberIdSchema.nullable(),
    closedAt: IsoDateTimeSchema.nullable(),
    /** Who closed the day once it is closed — staff (`manual`) or the rollover (`auto`, ADR-0029); null while open. */
    closeMode: DayCloseModeSchema.nullable(),
    /** The manager who signed off a day that rolled over by itself; null until then, and for a day staff closed. */
    signedOffByMemberId: MemberIdSchema.nullable(),
    signedOffAt: IsoDateTimeSchema.nullable(),
})
    .openapi('DayCloseReview');
/**
 * A closed business day (spec/13 §10, F-OPS-06): the totals snapshot taken at the close, kept for the record. A
 * closed day can no longer have voids; late corrections are refunds on a later day, so they never change this.
 */
export const BusinessDayCloseSchema = z
    .object({
    id: BusinessDayCloseIdSchema,
    venueId: z.string(),
    businessDay: LocalDateSchema,
    /** The member who closed it; null when the day rolled over by itself (`closeMode: auto`). */
    closedByMemberId: MemberIdSchema.nullable(),
    closedAt: IsoDateTimeSchema,
    ...dayTotalsShape,
    note: z.string().min(1).max(2000).nullable(),
    /** `manual`: staff closed it; `auto`: it closed at the venue's cutoff by itself (ADR-0029). */
    closeMode: DayCloseModeSchema,
    /** For an automatic close: the manager who reviewed and signed it off, and when; null until then. */
    signedOffByMemberId: MemberIdSchema.nullable(),
    signedOffAt: IsoDateTimeSchema.nullable(),
    signOffNote: z.string().min(1).max(2000).nullable(),
})
    .openapi('BusinessDayClose');
// ---------------------------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------------------------
/** Open a shift with its opening float (spec/13 §10, F-OPS-01). */
export const OpenShiftSchema = z
    .object({
    openingFloat: NonNegativeMoneySchema,
    /** The counter, from the venue's list (ADR-0032): the shift takes its name. Preferred over `counterName`. */
    counterId: CounterIdSchema.optional(),
    counterName: z.string().min(1).max(60).optional(),
    /** At hand-over (F-OPS-05): the closed shift whose counted cash this float carries over. */
    carriedFromShiftId: ShiftIdSchema.optional(),
})
    .openapi('OpenShiftRequest');
/** Record a cash movement on the open drawer (spec/13 §10, F-OPS-02). */
export const RecordCashMovementSchema = z
    .object({
    kind: CashMovementKindSchema,
    amount: PositiveMoneySchema,
    reason: z.string().min(1).max(200),
})
    .openapi('RecordCashMovementRequest');
/**
 * Close a shift with the blind count (spec/13 §10, F-OPS-03). The counted cash is required; the
 * counter card and UPI totals are confirmed against their terminals and apps. A `note` is required
 * when the variance exceeds `shifts.varianceNoteAmount`. `handOver` closes this shift and opens the
 * next one for the same counter with the counted cash carried over (F-OPS-05). The next shift is the caller's: a
 * hand-over is the incoming member counting the drawer they take custody of, so `shifts:own` may hand over another
 * member's shift to themselves (ADR-0029); ending another member's shift without a hand-over needs `shifts:close_any`.
 */
export const CloseShiftSchema = z
    .object({
    countedCash: NonNegativeMoneySchema,
    countedCounterCard: NonNegativeMoneySchema.optional(),
    countedCounterUpi: NonNegativeMoneySchema.optional(),
    note: z.string().min(1).max(2000).optional(),
    handOver: z.boolean().optional(),
})
    .openapi('CloseShiftRequest');
/** Close a business day once every shift is closed (spec/13 §10, F-OPS-06). */
export const CloseBusinessDaySchema = z
    .object({
    businessDay: LocalDateSchema,
    note: z.string().min(1).max(2000).optional(),
})
    .openapi('CloseBusinessDayRequest');
/** Sign off a business day that rolled over by itself (ADR-0029): the manager has reviewed it. */
export const SignOffBusinessDaySchema = z
    .object({
    businessDay: LocalDateSchema,
    note: z.string().min(1).max(2000).optional(),
})
    .openapi('SignOffBusinessDayRequest');
// ---------------------------------------------------------------------------------------------
// Routes (venue-scoped)
// ---------------------------------------------------------------------------------------------
const ShiftParamsSchema = VenueScopeParamsSchema.extend({ shiftId: ShiftIdSchema }).openapi('ShiftParams');
const ListShiftsQuerySchema = z
    .object({
    status: ShiftStatusSchema.optional(),
    businessDay: LocalDateSchema.optional(),
    /**
     * `true`: only closed shifts whose variance raised the owner's alert and whose business day is not closed yet —
     * the "Shift variance" items of the console's "What needs you" (F-OPS-07). The day close is the review that
     * settles them.
     */
    varianceAlerted: z.enum(['true', 'false']).optional(),
})
    .openapi('ListShiftsQuery');
const DayCloseQuerySchema = z
    .object({ businessDay: LocalDateSchema.optional() })
    .openapi('DayCloseQuery');
export const openShiftRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts',
    operationId: 'openShift',
    tags: ['shifts'],
    summary: 'Open a shift with a float (F-OPS-01)',
    description: 'Opens a till session for the venue and counts its opening float, which posts a balanced entry ' +
        'to the ledger (the drawer, debited; the venue safe, credited). At most one shift is open per ' +
        'venue. Requires `shifts:own`. Audited. Send an `Idempotency-Key`: a retry with it answers the shift ' +
        'already opened and posts no second float.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    request: {
        params: VenueScopeParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: OpenShiftSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(ShiftSchema, 'The open shift.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('A drawer is already open at that counter, the counter is archived or unknown, the caller already holds a drawer, a shared drawer is open (join it), the carried shift is not closed, or the venue does not track drawers (`shifts.enabled` is off).', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listShiftsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts',
    operationId: 'listShifts',
    tags: ['shifts'],
    summary: "List a venue's shifts (F-OPS-04)",
    description: "The venue's shifts, newest first, filtered by status and business day. `varianceAlerted=true` lists the closed " +
        'shifts whose variance alerted the owner and whose business day is still open (F-OPS-07). Requires `shifts:read_all`.',
    [PERMISSION_EXTENSION]: SHIFTS_READ_ALL,
    request: { params: VenueScopeParamsSchema, query: ListShiftsQuerySchema },
    responses: {
        200: jsonResponse(ShiftListSchema, "The venue's shifts, newest first."),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getCurrentShiftRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/current',
    operationId: 'getCurrentShift',
    tags: ['shifts'],
    summary: "The caller's open shift, or the venue's one (F-OPS-01)",
    description: 'The shift whose drawer the caller is on; else, when exactly one drawer is open at the venue, that one. 404 when none ' +
        'is open — or several are and none is the caller’s (ADR-0029: several counters may each have a drawer open).',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(ShiftSchema, 'The venue’s open shift, with its movements and X report.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No shift is open at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getShiftRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}',
    operationId: 'getShift',
    tags: ['shifts'],
    summary: 'Read one shift with its X/Z report (F-OPS-04)',
    [PERMISSION_EXTENSION]: SHIFTS_READ_ALL,
    request: { params: ShiftParamsSchema },
    responses: {
        200: jsonResponse(ShiftSchema, 'The shift, with its movements, tenders and variance.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such shift at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// A shift's cash timeline (spec/13 §10, F-OPS-04)
// ---------------------------------------------------------------------------------------------
/**
 * What happened on a shift's drawer (spec/13 §10): the opening float (a count, or the cash handed over from the previous
 * shift), each counter payment taken into it — a sale, a refund, a wallet top-up or refund, a pass sale or refund —
 * the pay-ins, pay-outs and drops, colleagues joining and leaving a shared drawer, the closing count with its variance,
 * and the counted cash handed over to the next shift. `other` is a counter tender line of any other entry kind.
 */
export const ShiftCashEventKindSchema = z
    .enum([
    'opening_float',
    'sale',
    'refund',
    'wallet_top_up',
    'wallet_refund',
    'pass_sale',
    'pass_refund',
    'pay_in',
    'pay_out',
    'drop',
    'member_joined',
    'member_left',
    'closing_count',
    'handed_over',
    'other',
])
    .openapi('ShiftCashEventKind', { description: 'One event on a shift’s drawer (spec/13 §10, F-OPS-04).' });
/**
 * The tender a payment event was taken in. Only `cash` moves the drawer; counter card, counter UPI and wallet are listed
 * with "all tenders" for reference and never change the expected drawer.
 */
export const ShiftCashTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi', 'wallet'])
    .openapi('ShiftCashTender', { description: 'The tender of a payment on a shift’s timeline; only cash is in the drawer.' });
/** Which events the timeline lists: the drawer's own (`cash`, the default) or every counter tender too (`all`). */
export const ShiftCashTimelineTendersSchema = z
    .enum(['cash', 'all'])
    .openapi('ShiftCashTimelineTenders', { description: '`cash` lists the drawer’s events; `all` adds counter card, counter UPI and wallet.' });
/** Who did it: the member who took the payment or recorded the event, with their name when it can be read. */
export const ShiftCashActorSchema = z
    .object({
    /** Null when the person is not a member of the organisation (a system post, a member who has left). */
    memberId: MemberIdSchema.nullable(),
    name: z.string().nullable(),
})
    .openapi('ShiftCashActor');
/**
 * One row of a shift's cash timeline. Every amount is read from the ledger lines the shift's reconciliation reads
 * (spec/06 §2.4 invariant 6) — `drawer_cash` for the float, movements, variance and hand-overs, and the counter tender
 * lines attributed to this drawer (ADR-0029) — so the running `balance` before the closing count is exactly the
 * shift's `expectedCash`.
 */
export const ShiftCashEventSchema = z
    .object({
    /** Stable and opaque: the ledger line, movement or member row it was read from. */
    id: z.string().min(1),
    kind: ShiftCashEventKindSchema,
    at: IsoDateTimeSchema,
    /** The tender of a payment event; null for the drawer's own events (float, movements, count, members). */
    tender: ShiftCashTenderSchema.nullable(),
    /**
     * The event's amount, signed from the drawer's side — `+` money in, `−` money out. A closing count's is what was
     * counted; null for a member joining or leaving.
     */
    amount: MoneySchema.nullable(),
    /** What it did to the expected drawer: the cash amount, the variance at the count, zero for any other tender. */
    drawerChange: MoneySchema,
    /** The expected drawer after this event — the running balance. After the closing count it is what was counted. */
    balance: MoneySchema,
    /** Who took the payment or recorded the event; null when nobody is recorded. */
    by: ShiftCashActorSchema.nullable(),
    /** The sale a sale or refund belongs to (open it in Orders), else null. */
    saleId: SaleIdSchema.nullable(),
    /** That sale's order number for its business day ("#0421", F-FNB-04), when allocated. */
    orderNumber: z.int().min(1).nullable(),
    /** A movement's reason, or the ledger entry's memo for `other`; null otherwise. */
    reason: z.string().nullable(),
    /**
     * A cash sale's cash as the counter handled it (ADR-0049): what the customer handed over and the change given back,
     * so `cashReceived − changeGiven` is the event's `amount`. Null for every other event. A payment taken without
     * "cash received" counts as handed over exactly.
     */
    cashReceived: MoneySchema.nullable(),
    changeGiven: MoneySchema.nullable(),
    /** The previous shift (an opening float handed over) or the next one (`handed_over`), else null. */
    otherShiftId: ShiftIdSchema.nullable(),
    /** At the closing count: what the ledger expected, what was counted, and `counted − expected`. Null otherwise. */
    expected: MoneySchema.nullable(),
    counted: MoneySchema.nullable(),
    variance: MoneySchema.nullable(),
})
    .openapi('ShiftCashEvent');
export const ShiftCashTimelineSchema = paginated(ShiftCashEventSchema, 'ShiftCashTimeline');
export const ShiftCashTimelineQuerySchema = PageQuerySchema.extend({
    tenders: ShiftCashTimelineTendersSchema.default('cash'),
    orderNumber: z.coerce.number().int().min(1).max(99_999).optional().openapi({ description: 'Only the events of the sale with this order number: its payment and its refunds.' }),
})
    .strict()
    .openapi('ShiftCashTimelineQuery');
export const getShiftCashTimelineRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}/cash-timeline',
    operationId: 'getShiftCashTimeline',
    tags: ['shifts'],
    summary: 'A shift’s cash timeline with the running drawer balance (F-OPS-04)',
    description: 'Every event on the shift’s drawer, oldest first: the opening float, each counter payment taken into it (sale, ' +
        'refund, wallet top-up, pass sale…) with its order number and who took it, the pay-ins, pay-outs and drops, ' +
        'colleagues joining and leaving a shared drawer, the closing count with its variance and the hand-over — each with ' +
        'the expected drawer after it. Read from the same ledger lines the close reads, so the balance before the count is ' +
        'the shift’s `expectedCash`. `tenders=all` adds counter card, counter UPI and wallet payments (never in the ' +
        'drawer’s balance); `orderNumber` narrows to one sale. Requires `shifts:own` for a shift the caller opened or was on; ' +
        'any other shift needs `shifts:read_all`. Cursor-paginated.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    [OTHERS_PERMISSION_EXTENSION]: SHIFTS_READ_ALL,
    request: { params: ShiftParamsSchema, query: ShiftCashTimelineQuerySchema },
    responses: {
        200: jsonResponse(ShiftCashTimelineSchema, 'The shift’s drawer events, oldest first, with the running expected drawer.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('The caller lacks `shifts:own`, or reads another member’s shift and lacks `shifts:read_all`.', 'forbidden'),
        404: problemResponse('No such shift at this venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const recordCashMovementRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}/movements',
    operationId: 'recordCashMovement',
    tags: ['shifts'],
    summary: 'Record a cash movement (F-OPS-02)',
    description: 'Records a pay-in, a pay-out or a drop to the safe on the open drawer, with a reason. Each posts ' +
        'a balanced entry to the ledger. Requires `shifts:cash_movements`. Audited.',
    [PERMISSION_EXTENSION]: SHIFTS_CASH_MOVEMENTS,
    request: {
        params: ShiftParamsSchema,
        body: { content: { 'application/json': { schema: RecordCashMovementSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(ShiftSchema, 'The shift after the movement.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such shift at this venue.', 'not-found'),
        409: problemResponse('The shift is closed.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const closeShiftRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}/close',
    operationId: 'closeShift',
    tags: ['shifts'],
    summary: 'Close a shift with the blind count (F-OPS-03, F-OPS-05)',
    description: 'Records the counted cash, and the counter card and UPI totals, computes the variance against ' +
        'what the ledger expected, and closes the shift. The variance is posted to the ledger, never a ' +
        'balance edited (spec/06 §2.4). A note is required when the variance exceeds ' +
        '`shifts.varianceNoteAmount`. `handOver` opens the next shift with the counted cash carried over: the ' +
        'counted cash moves from this shift’s drawer to the next one’s in one balanced entry (F-OPS-05). ' +
        'Requires `shifts:own` for the caller’s own shift. Another member’s shift: `shifts:own` hands it over to the ' +
        'caller (the incoming member counts the drawer and the next shift is theirs, ADR-0029); ending it without a ' +
        'hand-over needs `shifts:close_any`. Audited. Send an `Idempotency-Key`: a retry with it answers the close already made.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    [OTHERS_PERMISSION_EXTENSION]: SHIFTS_CLOSE_ANY,
    request: {
        params: ShiftParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CloseShiftSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(ShiftSchema, 'The closed shift, with its variance per tender.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('The caller lacks `shifts:own`, or ends another member’s shift without a hand-over and lacks `shifts:close_any`.', 'forbidden'),
        404: problemResponse('No such shift at this venue.', 'not-found'),
        409: problemResponse('The shift is already closed, or a note is required for the variance.', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getBusinessDayCloseRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/day-close',
    operationId: 'getBusinessDayClose',
    tags: ['shifts'],
    summary: "Review a business day's takings (F-OPS-06)",
    description: "The business day's review (spec/13 §10): takings by tender and category, refunds, voids, discounts, comps, " +
        'receivables raised, pass sales and wallet top-ups — the money read from the ledger in the day’s window (the ' +
        'venue’s cutoff, in its timezone) — its shifts with each variance and cashier, and whether every shift is ' +
        'closed. Defaults to the current business day. Requires `shifts:day_close`.',
    [PERMISSION_EXTENSION]: SHIFTS_DAY_CLOSE,
    request: { params: VenueScopeParamsSchema, query: DayCloseQuerySchema },
    responses: {
        200: jsonResponse(DayCloseReviewSchema, 'The business day before it is closed.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const closeBusinessDayRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/day-close',
    operationId: 'closeBusinessDay',
    tags: ['shifts'],
    summary: 'Close a business day (F-OPS-06)',
    description: 'Closes the business day once every shift is closed, keeping a snapshot of the review totals. Refused with ' +
        '409 while a shift of the day is open; the problem names each open shift and its cashier. A closed day can no ' +
        'longer have voids (`POST …/sales/{id}/cancel` answers 409); late corrections become refunds on a later day. ' +
        'Sends the day summary (N-27). Requires `shifts:day_close`. Audited. Send an `Idempotency-Key`: a retry with it ' +
        'answers the close already made.',
    [PERMISSION_EXTENSION]: SHIFTS_DAY_CLOSE,
    request: {
        params: VenueScopeParamsSchema,
        headers: IdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CloseBusinessDaySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(BusinessDayCloseSchema, 'The closed business day.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('A shift of the day is still open (named with its cashier), or the day is already closed.', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The business day as the counter sees it, and signing off an automatic close (ADR-0029)
// ---------------------------------------------------------------------------------------------
/**
 * Who holds the drawer right now (ADR-0029: a shift is drawer custody): the open shift in brief, with the cashier's
 * name, for the console's status strip ("Shift: Riya · since 14:02 · float ₹2,000").
 */
/** Someone on a drawer now, by name (ADR-0029). */
export const DrawerMemberSchema = z
    .object({ memberId: MemberIdSchema, name: z.string().nullable() })
    .openapi('DrawerMember');
export const DrawerCustodySchema = z
    .object({
    shiftId: ShiftIdSchema,
    counterName: z.string().min(1).max(60).nullable(),
    openedByMemberId: MemberIdSchema.nullable(),
    /** The cashier's name, when the member still exists; null otherwise. */
    openedByName: z.string().nullable(),
    openedAt: IsoDateTimeSchema,
    openingFloat: NonNegativeMoneySchema,
    /** The business day the shift opened in; earlier than today's when it spans the cutoff. */
    businessDay: LocalDateSchema,
    /** Everyone on the drawer now, with their names — the opener, and colleagues on a shared drawer (ADR-0029). */
    members: z.array(DrawerMemberSchema),
})
    .openapi('DrawerCustody');
/**
 * The venue's business day now (ADR-0029): which day it is, when it rolls over and how (`shifts.dayCloseMode`), whether
 * the venue tracks drawers (`shifts.enabled`), who holds the drawer, and the days that need a manager — an automatic
 * close not signed off yet, or yesterday still open under manual close. A day exists because time passes: nobody opens
 * it.
 */
export const BusinessDayStatusSchema = z
    .object({
    venueId: z.string(),
    businessDay: LocalDateSchema,
    /** When this business day began: the cutoff on its date, in the venue's timezone (DST-safe). */
    startedAt: IsoDateTimeSchema,
    /** When it rolls over to the next: the cutoff on the next date. */
    rollsOverAt: IsoDateTimeSchema,
    /** The venue's cutoff (`HH:MM`, default 06:00): the venue profile's `businessDayCutoff`. */
    cutoff: LocalTimeSchema,
    timezone: TimezoneSchema,
    dayCloseMode: DayCloseModeSchema,
    /** `shifts.enabled`: whether the venue counts a drawer per shift. */
    shiftsEnabled: z.boolean(),
    /** Whether the business day before this one is closed (by staff or by its rollover). */
    previousDayClosed: z.boolean(),
    /** Days that rolled over by themselves and wait for a manager's sign-off, newest first (at most 14). */
    awaitingSignOff: z.array(LocalDateSchema),
    /**
     * The caller's drawer, else the one drawer open. Null when none is (or several are, none the caller's), and when the
     * caller may not see the drawer (neither `shifts:own` nor `shifts:read_all`).
     */
    custody: DrawerCustodySchema.nullable(),
    /**
     * Every drawer open now, oldest first — one per counter, several at once (ADR-0029). Empty when none is open, and
     * to a member who may not see the drawer.
     */
    drawers: z.array(DrawerCustodySchema),
    /** `shifts.cashHandling`: how the venue handles cash between people. */
    cashHandling: CashHandlingSchema,
    /** `shifts.blindClose`: the drawer is counted without seeing the expected amount. */
    blindClose: z.boolean(),
    /** `shifts.varianceNoteAmount`: a count off by more than this needs a note to close. */
    varianceNoteAmount: MoneySchema,
})
    .openapi('BusinessDayStatus');
export const getBusinessDayStatusRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/business-day',
    operationId: 'getBusinessDayStatus',
    tags: ['shifts'],
    summary: 'The business day now, and who holds the drawer (ADR-0029)',
    description: 'The venue’s current business day, when it rolls over and whether it closes by itself (`shifts.dayCloseMode`), ' +
        'whether drawers are tracked (`shifts.enabled`), the open shift with its cashier and float (to a member with ' +
        '`shifts:own` or `shifts:read_all`), and the days waiting for a manager. Read by the console’s status strip. ' +
        'Requires `venues:read`.',
    [PERMISSION_EXTENSION]: VENUES_READ,
    request: { params: VenueScopeParamsSchema },
    responses: {
        200: jsonResponse(BusinessDayStatusSchema, 'The business day now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const signOffBusinessDayRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/day-close/sign-off',
    operationId: 'signOffBusinessDay',
    tags: ['shifts'],
    summary: 'Sign off a day that closed by itself (ADR-0029)',
    description: 'A manager’s sign-off of a business day that rolled over at the cutoff (`closeMode: auto`): the review was read ' +
        'and accepted, with an optional note. It changes no figure — the close’s snapshot stands. Refused with 409 for a ' +
        'day that is not closed, one staff closed (that close was its sign-off), or one already signed off. Requires ' +
        '`shifts:day_close`. Audited.',
    [PERMISSION_EXTENSION]: SHIFTS_DAY_CLOSE,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: SignOffBusinessDaySchema } }, required: true },
    },
    responses: {
        200: jsonResponse(BusinessDayCloseSchema, 'The signed-off business day.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('The day is not closed, was closed by staff, or is already signed off.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const joinShiftRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}/join',
    operationId: 'joinShift',
    tags: ['shifts'],
    summary: 'Join a shared drawer (ADR-0029)',
    description: 'With `shifts.cashHandling: shared_drawer`, a colleague joins the open shift: what they ring up goes into its drawer, ' +
        'and its count and variance stay the shift’s. Refused with 409 elsewhere, on a closed shift, or when the caller is ' +
        'already on a drawer. Requires `shifts:own`. Audited.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    request: { params: ShiftParamsSchema },
    responses: {
        200: jsonResponse(ShiftSchema, 'The shift, with the caller on it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such shift at this venue.', 'not-found'),
        409: problemResponse('The venue does not share a drawer, the shift is closed, or the caller is on a drawer already.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const leaveShiftRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/shifts/{shiftId}/leave',
    operationId: 'leaveShift',
    tags: ['shifts'],
    summary: 'Leave a shared drawer (ADR-0029)',
    description: 'The caller steps off a shared drawer; what they ring up after no longer goes into it. The last one on a drawer ' +
        'cannot leave it — they end the shift or hand it over. Requires `shifts:own`. Audited.',
    [PERMISSION_EXTENSION]: SHIFTS_OWN,
    request: { params: ShiftParamsSchema },
    responses: {
        200: jsonResponse(ShiftSchema, 'The shift, without the caller.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such shift at this venue.', 'not-found'),
        409: problemResponse('The caller is not on the shift, or is the last one on it.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Open drawer (F-OPS-09; spec/13 §11, spec/09 §6.2)
// ---------------------------------------------------------------------------------------------
/**
 * A drawer kick sent to the desk device (F-OPS-09): the `station.drawer.open` command, the printer the drawer is
 * wired to, and the shift the audit row names. The kick is best-effort hardware: it expires 30 seconds after it was
 * sent and the console follows it on `command.updated`; it never changes a recorded amount (rule 6) and is no ledger
 * entry (rule 7).
 */
export const DrawerOpenSchema = z
    .object({
    commandId: StationCommandIdSchema,
    status: StationCommandStatusSchema,
    printerId: PrinterIdSchema,
    /** The open shift at the venue when the drawer was opened; null when none was open. */
    shiftId: ShiftIdSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
})
    .openapi('DrawerOpen');
export const OpenDrawerSchema = z
    .object({
    /** Why the drawer is opened with no sale behind it ("change for a 500"); kept on the audit row. */
    reason: z.string().trim().min(1).max(200).optional(),
})
    .openapi('OpenDrawerRequest');
export const openDrawerRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/drawer/open',
    operationId: 'openDrawer',
    tags: ['shifts'],
    summary: 'Open the cash drawer (F-OPS-09)',
    description: 'A no-sale drawer kick: `station.drawer.open` to the desk device whose receipt printer has the cash drawer. ' +
        'Refused without `shifts:drawer_open`, and then nothing reaches the desk device. Audited with the member, the ' +
        'venue, the open shift and the time. The command expires after 30 seconds; its outcome arrives on ' +
        '`command.updated`.',
    [PERMISSION_EXTENSION]: SHIFTS_DRAWER_OPEN,
    request: {
        params: VenueScopeParamsSchema,
        body: { content: { 'application/json': { schema: OpenDrawerSchema } }, required: false },
    },
    responses: {
        202: jsonResponse(DrawerOpenSchema, 'The drawer kick, on its way.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        409: problemResponse('No desk printer at this venue has a cash drawer.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=shifts.js.map